/**
 * App Initializer Component
 * Ensures files are loaded from backend when app starts.
 * Checks organization storage connection for file upload capability.
 *
 * F11 (chat-ui catch-up plan): the chat_sessions backend table is
 * unwritten in the mission-centric model — the legacy session-loading call
 * has been removed. Conversation history surfaces via `useConversationHistory`
 * (which reads /v1/executions).
 */

import { useEffect } from 'react'
import { useFileStore } from '../hooks/useFiles'
import { useOrgStorageStore } from '../hooks/useOrgStorage'

interface AppInitializerProps {
  children: React.ReactNode
}

export function AppInitializer({ children }: AppInitializerProps) {
  // Use the store directly without destructuring to avoid TypeScript issues
  const fileStore = useFileStore()
  const orgStorageStore = useOrgStorageStore()

  useEffect(() => {
    console.log('[AppInitializer] Initializing app...')

    // Load files from backend when component mounts
    // Session cookies are automatically sent with credentials: 'include'
    console.log('[AppInitializer] Loading files from backend...')
    const loadFiles = (fileStore as any).loadFiles
    if (typeof loadFiles === 'function') {
      loadFiles().then(() => {
        console.log('[AppInitializer] Files loaded successfully')
      }).catch((error: any) => {
        console.error('[AppInitializer] Failed to load files:', error)
      })
    } else {
      console.error('[AppInitializer] loadFiles is not available')
    }

    // Check if organization has connected storage
    // This enables the file attachment feature in chat when storage is available
    console.log('[AppInitializer] Checking organization storage connection...')
    const checkStorage = orgStorageStore.checkStorageConnection
    if (typeof checkStorage === 'function') {
      checkStorage().then((isConnected) => {
        if (isConnected) {
          console.log('[AppInitializer] Organization storage connected - file attachments enabled')
        } else {
          console.log('[AppInitializer] No organization storage connected - file attachments disabled')
        }
      }).catch((error: any) => {
        console.error('[AppInitializer] Failed to check storage connection:', error)
      })
    }
  }, []) // Empty dependency array - run only once on mount

  return <>{children}</>
}
